/**
 * Server-rendered SEO head builder for route `head()` options.
 *
 * Every static route file calls buildRouteHead("<path>") so that title,
 * description, canonical, hreflang and Open Graph / Twitter tags exist in the
 * SSR HTML (crawlers that don't execute JavaScript see them too).
 *
 * Per-page title/description come from src/generated/seoRouteMeta.ts (generated
 * by `python3 scripts/generate-seo-route-meta.py`, which reads the SEO each page
 * renders). Pages without an entry fall back to language-aware defaults.
 */
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "@/config/site";
import { getLangFromPath, normalizePath } from "./hreflang";
import { SEO_ROUTE_META } from "@/generated/seoRouteMeta";
import { SEO_ROUTE_OVERRIDES } from "./seoRouteOverrides";
import { resolveCanonicalPath } from "@/lib/seo/canonicalAliases";
import { getHubOgImage } from "./hubOgImages";

export interface RouteHeadOverrides {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogType?: string;
  noIndex?: boolean;
}


const DEFAULT_SEO: Record<string, { title: string; description: string }> = {
  en: {
    title: "QbD Group — Life Sciences Consulting Experts",
    description:
      "QbD Group accelerates life sciences innovation with 700+ experts in regulatory affairs, quality assurance, clinical trials and compliance consulting.",
  },
  es: {
    title: "QbD Group — Consultoría Experta en Ciencias de la Vida",
    description:
      "QbD Group impulsa la innovación en ciencias de la vida con más de 700 expertos en asuntos regulatorios, calidad, ensayos clínicos y cumplimiento normativo.",
  },
  "zh-cn": {
    title: "QbD Group — 生命科学咨询专家",
    description:
      "QbD Group 拥有 700 多位专家，为生命科学企业提供法规事务、质量保证、临床试验与合规咨询服务。",
  },
};

const absolute = (url: string): string =>
  /^https?:\/\//i.test(url) ? url : `${SITE_URL}${url.startsWith("/") ? "" : "/"}${url}`;

/**
 * Dev-server-only URLs (Vite serves un-bundled sources at /src/... and /@fs/...)
 * and blob/data URLs 404 for crawlers in production, so they can never be used
 * as a share image. Anything unusable falls back to the site card.
 */
const isShareableImage = (url: string): boolean => {
  if (!url) return false;
  const path = url.replace(/^https?:\/\/[^/]+/i, "");
  if (/^(?:data|blob):/i.test(url)) return false;
  if (/^\/(?:src|@fs|@vite|node_modules)\//.test(path)) return false;
  if (path.startsWith("/placeholder")) return false;
  return true;
};

/**
 * Some SEO entries were authored with accented Spanish paths while the live
 * URLs are accent-free (and vice versa). Match either form so an accent-free
 * route still gets its own title/description instead of the site default.
 */
const deaccentPath = (value: string): string =>
  value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const buildDeaccentIndex = <T>(source: Record<string, T>): Record<string, T> => {
  const index: Record<string, T> = {};
  for (const [key, value] of Object.entries(source)) {
    const alias = deaccentPath(key);
    if (!(alias in index)) index[alias] = value;
  }
  return index;
};

let storedAliasIndex: Record<string, (typeof SEO_ROUTE_META)[string]> | null = null;
let manualAliasIndex: Record<string, (typeof SEO_ROUTE_OVERRIDES)[string]> | null = null;

export function buildRouteHead(path: string, overrides: RouteHeadOverrides = {}) {
  const normPath = normalizePath(path);
  const lang = getLangFromPath(normPath);
  const fallback = DEFAULT_SEO[lang] ?? DEFAULT_SEO.en;
  const alias = deaccentPath(normPath);
  storedAliasIndex ??= buildDeaccentIndex(SEO_ROUTE_META);
  manualAliasIndex ??= buildDeaccentIndex(SEO_ROUTE_OVERRIDES);
  // The legacy generated record remains a valid source for unchanged offering copy.
  // Public metadata always advertises the new URL/name, never its old canonical.
  const toxicologyLegacyPath = normPath
    .replace(/\/(en|zh-cn)\/services\/regulatory-toxicology(?=\/|$)/, "/$1/services/toxicology")
    .replace(/\/es\/servicios\/toxicologia-regulatoria(?=\/|$)/, "/es/servicios/toxicologia");
  const isToxicologyRename = toxicologyLegacyPath !== normPath;
  const legacyStored = isToxicologyRename ? SEO_ROUTE_META[toxicologyLegacyPath] : undefined;
  const stored = SEO_ROUTE_META[normPath] ?? storedAliasIndex[alias] ?? legacyStored;
  const manual = SEO_ROUTE_OVERRIDES[normPath] ?? manualAliasIndex[alias];


  const serviceName = lang === "es" ? "Toxicología Regulatoria" : lang === "zh-cn" ? "法规毒理学" : "Regulatory Toxicology";
  const isToxicologyHub = /^\/(?:en|zh-cn)\/services\/regulatory-toxicology$|^\/es\/servicios\/toxicologia-regulatoria$/.test(normPath);
  const renamedTitle = isToxicologyRename && stored === legacyStored && stored?.title
    ? stored.title.replace(/Toxicology Services|Toxicology|Servicios de toxicología|毒理学服务|毒理学/g, serviceName)
    : undefined;
  const title = overrides.title ?? manual?.title ?? (isToxicologyHub ? `${serviceName} | QbD Group` : renamedTitle) ?? stored?.title ?? fallback.title;
  const description =
    overrides.description ?? manual?.description ?? stored?.description ?? fallback.description;
  const storedCanonical = SEO_ROUTE_META[normPath]?.canonical;
  const canonical = absolute(
    overrides.canonical ?? (isToxicologyRename ? resolveCanonicalPath(normPath) : storedCanonical ?? resolveCanonicalPath(normPath)),
  );

  const noIndex = overrides.noIndex ?? stored?.noIndex ?? false;
  const ogType = overrides.ogType ?? stored?.ogType ?? "website";

  // Per-route share image: explicit override (dynamic content covers) wins over
  // the hub hero card, which wins over the crawled per-page hero and finally the
  // site-wide card.
  const hubImage = getHubOgImage(normPath);
  const candidate = [overrides.ogImage, manual?.ogImage, hubImage, stored?.ogImage].find(
    (url): url is string => typeof url === "string" && isShareableImage(url.trim()),
  );
  const image = absolute((candidate ?? DEFAULT_OG_IMAGE).trim());
  const isCustomImage = image !== absolute(DEFAULT_OG_IMAGE);
  // Hub cards are generated at exactly 1200x630.
  const isHubImage = !!hubImage && candidate === hubImage;
  const imageAlt = isCustomImage
    ? (overrides.ogImageAlt ?? manual?.ogImageAlt ?? stored?.ogImageAlt ?? title)
    : `${SITE_NAME} — ${title}`;
  const width = isCustomImage && !isHubImage ? (overrides.ogImageWidth ?? stored?.ogImageWidth) : 1200;
  const height = isCustomImage && !isHubImage ? (overrides.ogImageHeight ?? stored?.ogImageHeight) : 630;


  const meta = [
    { title },
    { name: "description", content: description },
    { name: "robots", content: noIndex ? "noindex, nofollow" : "index, follow" },
    { property: "og:type", content: ogType },
    { property: "og:url", content: canonical },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: image },
    { property: "og:image:alt", content: imageAlt },
    ...(width ? [{ property: "og:image:width", content: String(width) }] : []),
    ...(height ? [{ property: "og:image:height", content: String(height) }] : []),
    { property: "og:site_name", content: SITE_NAME },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: image },
    { name: "twitter:image:alt", content: imageAlt },
  ];

  // canonical + hreflang are emitted once by the root route (see __root.tsx):
  // TanStack concatenates `links` across matched routes, so emitting them here
  // would duplicate them on every nested route.
  return { meta };
}

